import {hasFeatureFlag} from './FeatureUtil';

const TIMESHEET_APPROVAL_NEW_STATUSES_FLAG = 'timesheet_approval_new_statuses';
const USE_SAGE_SERVICE_FOR_TIME_REGS_FLAG = 'use_sage_service_for_time_regs';

/**
 * Whether this company's time registrations are served by Sage Intacct.
 *
 * The one definition of "Sage" for web-front's approval surfaces (PIKA-793), so the flag key is
 * spelled once. Exported because one caller needs the bare Sage answer rather than the combined
 * one below: `auditLogReport.canOpenAuditLogReport`. That report's flag is deliberately
 * independent of the new-statuses rollout, so composing the two there would re-couple them.
 *
 * Flag only — deliberately NOT `SageUtil.isSageAccount()`; see `hasNewApprovalStatuses` below for
 * why, and do not "align" this with SageUtil.
 */
export const isSageTimeRegCompany = (): boolean => hasFeatureFlag(USE_SAGE_SERVICE_FOR_TIME_REGS_FLAG);

/**
 * Whether this company is on the new approval statuses.
 *
 * Two flags, not one. `timesheet_approval_new_statuses` is going into
 * `rolledOutFeatureFlags` (PIKA-690), which turns it on for *every* company and makes it
 * impossible to switch off for an individual one — so the Sage carve-out Clare asked for on
 * that ticket cannot live at the flag layer and lives here instead (PIKA-793).
 *
 * Sage is judged by `use_sage_service_for_time_regs` alone, deliberately NOT by
 * `SageUtil.isSageAccount()`, which additionally requires the SAGE_PSA_RESTRICTED module. The
 * server is the authority on this question and judges it from the flag alone, in both
 * `ApprovalStatusUtil.java` and `ApprovalStatusCompat.js`. A front end that also demanded the
 * module would call a company non-Sage that the server treats as Sage, and show it UI for
 * actions the server then refuses — the exact failure this helper exists to prevent. Do not
 * "align" this with SageUtil.
 *
 * This file is deliberately a leaf: FeatureUtil only, no Relay, no constants, no modal graph.
 * It was extracted from TimeApprovalLogic.js — which imports all three — precisely so My
 * Timesheets, the team timesheet and the reports tab can reach it without dragging that in.
 * Mirrors the backend's `forecast/util/ApprovalStatusUtil.java` in both name and semantics, so
 * all three layers are greppable as one idea.
 */
export const hasNewApprovalStatuses = (): boolean =>
	hasFeatureFlag(TIMESHEET_APPROVAL_NEW_STATUSES_FLAG) && !isSageTimeRegCompany();
